import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { ImageUpload, SingleImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

export default function VendorProfileEdit() {
    const [cats, setCats] = useState([]);
    const [elements, setElements] = useState([]);
    const [form, setForm] = useState({
        business_name: "", logo_url: "", photos: [],
        description: "", categories: [], primary_category_id: "",
        service_area: "", service_radius_miles: 50,
        website: "", social_links: {},
        freebies: "",
        interactive_booth_elements: "",
        raffle_prize: "",
        // Direct contact phone (lives on users.phone) + inline SMS opt-in.
        // Stored as a formatted "(555) 123-4567" string in local state; the
        // save handler normalizes to E.164 on the wire.
        phone: "",
        sms_opt_in: false,
        notification_preferences: { categories: [], geography: "", method: "email", frequency: "immediate" },
    });
    const [busy, setBusy] = useState(false);
    // Captures the category the vendor was created with. Once set, the field is
    // read-only on the profile page — vendors must email support to change it.
    const [initialCategoryId, setInitialCategoryId] = useState("");

    useEffect(() => { (async () => {
        const [p, c, be] = await Promise.all([
            api.get("/vendor/profile"),
            api.get("/categories"),
            api.get("/booth-elements"),
        ]);
        if (p.data) {
            // Legacy data may store interactive_booth_elements as an array of
            // ids. Backend migrates on startup but coerce defensively here too.
            const ibe = p.data.interactive_booth_elements;
            const ibeStr = Array.isArray(ibe)
                ? ibe.map((id) => (be.data.find((el) => el.id === id) || {}).name).filter(Boolean).join(", ")
                : (ibe || "");
            setForm({
                ...form,
                ...p.data,
                interactive_booth_elements: ibeStr,
                phone: formatPhoneDisplay(p.data.phone || ""),
                sms_opt_in: !!p.data.sms_opt_in,
                notification_preferences: p.data.notification_preferences || form.notification_preferences,
            });
            const locked = p.data.primary_category_id || (p.data.categories || [])[0] || "";
            setInitialCategoryId(locked);
        }
        setCats(c.data);
        setElements(be.data);
    })(); /* eslint-disable-next-line */ }, []);

    // Format raw digits / E.164 into "(555) 123-4567". Used for both the input
    // onChange handler and the initial value coming back from the server (which
    // is stored as `+15551234567`).
    function formatPhoneDisplay(raw) {
        const d = String(raw || "").replace(/\D/g, "").replace(/^1/, "").slice(0, 10);
        if (d.length === 0) return "";
        if (d.length < 4) return `(${d}`;
        if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }

    // Append a quick-pick element name to the free-text IBE field. Dedupes
    // case-insensitively against what's already typed (split on commas) so
    // double-clicking a chip is a no-op.
    const appendElement = (name) => {
        const cur = (form.interactive_booth_elements || "").trim();
        const parts = cur ? cur.split(",").map((s) => s.trim()).filter(Boolean) : [];
        if (parts.some((p) => p.toLowerCase() === name.toLowerCase())) return;
        const next = [...parts, name].join(", ");
        setForm({ ...form, interactive_booth_elements: next });
    };

    const toggleCat = (id, key = "categories") => {
        const cur = form[key] || [];
        const has = cur.includes(id);
        setForm({ ...form, [key]: has ? cur.filter((x) => x !== id) : [...cur, id] });
    };

    const toggleNotifCat = (id) => {
        const cur = form.notification_preferences.categories || [];
        const has = cur.includes(id);
        setForm({ ...form, notification_preferences: {
            ...form.notification_preferences,
            categories: has ? cur.filter((x) => x !== id) : [...cur, id],
        }});
    };

    const save = async (e) => {
        e.preventDefault();
        // Normalize website on save: prepend https:// when the user typed
        // a bare domain. Mirrors the backend validator so the locally-held
        // form state — and any consumer that reads from it before the next
        // GET — immediately reflects the canonical URL. Belt-and-suspenders
        // with the backend so a stale-cached public profile that re-renders
        // from form data (or a not-yet-redeployed backend) still sees the
        // protocol-prefixed value.
        const w = (form.website || "").trim();
        const normalized = !w || /^https?:\/\//i.test(w) ? w : "https://" + w;

        // SMS opt-in requires a valid 10-digit US phone. Catch this in the FE
        // so we surface a friendly toast instead of a generic 400.
        const phoneDigits = (form.phone || "").replace(/\D/g, "");
        if (form.sms_opt_in && phoneDigits.length !== 10) {
            toast.error("Add a valid 10-digit US mobile number to opt in to SMS.");
            return;
        }

        const payload = {
            ...form,
            website: normalized,
            // Send phone as raw digits — backend normalizes to E.164.
            phone: phoneDigits || "",
            sms_opt_in: !!form.sms_opt_in,
        };
        if (normalized !== form.website) {
            setForm({ ...form, website: normalized });
        }
        setBusy(true);
        try {
            const res = await api.put("/vendor/profile", payload);
            // Re-seed from the server response so any further server-side
            // normalization (or other field coercion) is reflected in the UI.
            if (res?.data) setForm((f) => ({
                ...f,
                ...res.data,
                phone: formatPhoneDisplay(res.data.phone || ""),
                sms_opt_in: !!res.data.sms_opt_in,
                notification_preferences: res.data.notification_preferences || f.notification_preferences,
            }));
            toast.success("Profile saved");
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };

    return (
        <Shell>
            <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <div className="label-base">Vendor</div>
                        <h1 className="font-heading text-3xl md:text-4xl font-extrabold">Booth profile</h1>
                        <p className="text-brand-mute mt-2 text-sm">Edit your public profile and notification preferences.</p>
                    </div>
                    {form.id && (
                        <Link
                            to={`/vendors/${form.id}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="view-public-profile"
                            className="pill px-4 py-2 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone inline-flex items-center gap-2"
                        >
                            View public profile <ExternalLink size={14} />
                        </Link>
                    )}
                </div>

                <form onSubmit={save} className="mt-8 space-y-6" data-testid="vendor-profile-form">
                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-5">
                        <h3 className="font-heading font-bold">Brand & photos</h3>
                        <SingleImageUpload
                            value={form.logo_url}
                            onChange={(p) => setForm({ ...form, logo_url: p })}
                            label="Business logo"
                            testIdPrefix="vp-logo"
                        />
                        <ImageUpload
                            value={form.photos || []}
                            onChange={(p) => setForm({ ...form, photos: p })}
                            max={8}
                            label="Booth photos"
                            testIdPrefix="vp-photos"
                        />
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4">
                        <h3 className="font-heading font-bold">Service area & proximity</h3>
                        <p className="text-sm text-brand-mute">Tell us where you're based and how far you'll travel. We geocode this so you only get alerts about events within your radius — no spam, no missed leads.</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label-base">Home city</label>
                                <input data-testid="vp-home-city" className="input-base" placeholder="e.g. Arlington" value={form.home_city || ""} onChange={(e) => setForm({ ...form, home_city: e.target.value })} />
                            </div>
                            <div>
                                <label className="label-base">Home state</label>
                                <input data-testid="vp-home-state" className="input-base" placeholder="e.g. VA" value={form.home_state || ""} onChange={(e) => setForm({ ...form, home_state: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label-base">Service area (display only)</label>
                                <input data-testid="vp-area" placeholder="e.g. DC metro" className="input-base" value={form.service_area || ""} onChange={(e) => setForm({ ...form, service_area: e.target.value })} />
                            </div>
                            <div>
                                <label className="label-base">Travel radius (miles)</label>
                                <input data-testid="vp-radius" type="number" min={1} className="input-base" value={form.service_radius_miles || 50} onChange={(e) => setForm({ ...form, service_radius_miles: parseInt(e.target.value || "0", 10) })} />
                            </div>
                        </div>
                        {form.geocoded_home && (
                            <p className="text-xs text-brand-mute">📍 We placed you at <span className="font-semibold text-brand-ink">{form.geocoded_home}</span>. Save the form to refresh this.</p>
                        )}
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4">
                        <h3 className="font-heading font-bold">Public profile</h3>
                        <div>
                            <label className="label-base">Business name</label>
                            <input data-testid="vp-name" required className="input-base" value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-base">Description</label>
                            <textarea data-testid="vp-desc" rows={3} className="input-base !h-auto py-3" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-base">Website</label>
                            <input data-testid="vp-website" type="text" className="input-base" value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="mywebsite.com" />
                            <p className="text-xs text-brand-mute mt-1">Enter your website (e.g. mywebsite.com). We'll automatically add https:// if needed.</p>
                        </div>
                        <div data-testid="vp-social-links-pod">
                            <label className="label-base">Social links <span className="text-brand-mute font-normal">(optional — only the ones you fill in are shown publicly)</span></label>
                            <div className="grid sm:grid-cols-2 gap-3 mt-1">
                                {[
                                    ["linkedin", "LinkedIn"],
                                    ["instagram", "Instagram"],
                                    ["facebook", "Facebook"],
                                    ["x", "X (Twitter)"],
                                ].map(([key, label]) => (
                                    <div key={key}>
                                        <label className="text-xs font-semibold text-brand-mute uppercase tracking-wider">{label}</label>
                                        <input
                                            data-testid={`vp-social-${key}`}
                                            type="url"
                                            className="input-base"
                                            placeholder="https://"
                                            value={(form.social_links || {})[key] || ""}
                                            onChange={(e) => setForm({
                                                ...form,
                                                social_links: { ...(form.social_links || {}), [key]: e.target.value },
                                            })}
                                        />
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-brand-mute mt-2">Paste the full URL to each profile. Hosts reviewing your application will see clickable icons for whichever platforms you've filled in.</p>
                        </div>
                        <div>
                            <label className="label-base">Booth Freebies</label>
                            <input data-testid="vp-freebies" className="input-base" value={form.freebies || ""} onChange={(e) => setForm({ ...form, freebies: e.target.value })} placeholder="e.g. 50% off first purchase, branded water bottles, $10 gift cards" />
                            <p className="text-xs text-brand-mute mt-1">Small swag and giveaways every attendee can take home. Auto-fills the note you send when expressing interest in an event. The more concrete and generous, the better.</p>
                        </div>
                        <div>
                            <label className="label-base">Raffle Prize</label>
                            <input data-testid="vp-raffle-prize" className="input-base" value={form.raffle_prize || ""} onChange={(e) => setForm({ ...form, raffle_prize: e.target.value })} placeholder='e.g. "high-end blender", "fitness tracker", "wellness gift basket"' />
                            <p className="text-xs text-brand-mute mt-1">Larger-ticket item attendees can <strong>enter to win</strong> at your booth during the event. Showcased on your vendor card and profile when set — a strong driver of foot traffic.</p>
                        </div>
                        <div>
                            <label className="label-base">Interactive booth elements</label>
                            <p className="text-xs text-brand-mute mb-2">What you'll bring or offer on-site. Type your own list, or tap a suggestion below to append it. Comma-separated.</p>
                            <textarea
                                data-testid="vp-ibe"
                                className="input-base !h-auto py-2"
                                rows={2}
                                placeholder="e.g. Chair massage station, body composition scans, customised wellness plans"
                                value={form.interactive_booth_elements || ""}
                                onChange={(e) => setForm({ ...form, interactive_booth_elements: e.target.value })}
                            />
                            {elements.length > 0 && (
                                <div className="mt-2">
                                    <div className="text-[11px] uppercase tracking-wider text-brand-mute font-bold mb-1.5">Quick picks</div>
                                    <div className="flex flex-wrap gap-2">
                                        {elements.map((el) => (
                                            <button
                                                type="button"
                                                key={el.id}
                                                data-testid={`vp-ibe-pick-${el.id}`}
                                                onClick={() => appendElement(el.name)}
                                                className="pill px-3 py-1.5 text-xs font-semibold border border-brand-line bg-white text-brand-ink hover:bg-brand-bone"
                                            >
                                                + {el.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="label-base">Your category <span className="text-brand-coral">*</span></label>
                            <p className="text-xs text-brand-mute mb-2">Each vendor serves a single category. This is the category you'll be listed under when applying to events.</p>
                            {initialCategoryId ? (
                                <div
                                    data-testid="vp-primary-category-locked"
                                    className="rounded-lg border border-brand-line bg-brand-bone px-4 py-3 max-w-md"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="font-heading font-semibold">
                                            {cats.find((c) => c.id === initialCategoryId)?.name || "Category"}
                                        </span>
                                        <span className="text-xs text-brand-mute italic">locked</span>
                                    </div>
                                    <p className="text-xs text-brand-mute mt-2">
                                        Need to change your category? Email{" "}
                                        <a
                                            data-testid="vp-category-support-mailto"
                                            href={`mailto:support@healthfairx.com?subject=${encodeURIComponent(
                                                `Category Change Request - ${form.business_name || ""}`.trim()
                                            )}&body=${encodeURIComponent(
                                                `Hi Support,\n\nPlease change my category from "${
                                                    cats.find((c) => c.id === initialCategoryId)?.name || initialCategoryId
                                                }" to: \n\nReason:\n\nThanks,\n${form.business_name || ""}`
                                            )}`}
                                            className="text-brand-forest font-semibold hover:underline"
                                        >
                                            support@healthfairx.com
                                        </a>{" "}
                                        and an admin will review your request.
                                    </p>
                                </div>
                            ) : (
                                <select
                                    data-testid="vp-primary-category"
                                    className="input-base max-w-md"
                                    value={form.primary_category_id || (form.categories && form.categories[0]) || ""}
                                    onChange={(e) => setForm({ ...form, primary_category_id: e.target.value, categories: e.target.value ? [e.target.value] : [] })}
                                >
                                    <option value="">Select a category…</option>
                                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            )}
                        </div>
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4" data-testid="vp-contact-sms-pod">
                        <h3 className="font-heading font-bold">Phone &amp; SMS notifications</h3>
                        <p className="text-sm text-brand-mute">Your direct contact number. Optionally opt in to short transactional texts — application approvals, payment reminders, day-of logistics. No marketing.</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label-base">Mobile phone</label>
                                <input
                                    data-testid="vp-phone"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel-national"
                                    className="input-base"
                                    placeholder="(555) 123-4567"
                                    value={form.phone || ""}
                                    onChange={(e) => setForm({ ...form, phone: formatPhoneDisplay(e.target.value) })}
                                />
                                <p className="text-xs text-brand-mute mt-1">US numbers only. Leave blank to remove on file.</p>
                            </div>
                        </div>
                        <label className="mt-3 flex items-start gap-3 cursor-pointer select-none">
                            <input
                                data-testid="vp-sms-consent-checkbox"
                                type="checkbox"
                                checked={!!form.sms_opt_in}
                                onChange={(e) => setForm({ ...form, sms_opt_in: e.target.checked })}
                                className="mt-1 h-4 w-4 rounded border-brand-line text-brand-forest focus:ring-brand-forest"
                            />
                            <span className="text-sm text-brand-ink leading-relaxed">
                                Yes, I would like to receive automated text messages from <strong>HealthFairX</strong> about event reminders and vendor application status updates. I understand message frequency varies and is event-driven (typically a few messages per month).
                            </span>
                        </label>
                        <div className="text-xs text-brand-mute space-y-1.5 leading-relaxed">
                            <p><strong className="text-brand-ink">Message Frequency:</strong> Event-driven. Most users receive a few messages per month.</p>
                            <p><strong className="text-brand-ink">Standard Rates:</strong> Message and data rates may apply depending on your mobile phone service plan.</p>
                            <p><strong className="text-brand-ink">Help &amp; Stop:</strong> Reply <strong>HELP</strong> for help, or <strong>STOP</strong> to cancel at any time.</p>
                            <p>Consent is not required to use the HealthFairX platform. See our <Link to="/terms" className="underline" data-testid="vp-sms-terms-link">Terms of Service</Link> and <Link to="/privacy" className="underline" data-testid="vp-sms-privacy-link">Privacy Policy</Link>.</p>
                        </div>
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-6 space-y-4">
                        <h3 className="font-heading font-bold">Notification preferences</h3>
                        <p className="text-sm text-brand-mute">Pick the categories that should trigger an alert. We'll only notify you about events whose desired categories overlap with these <span className="font-semibold text-brand-ink">and</span> that fall inside the travel radius you set above.</p>
                        <div className="flex flex-wrap gap-2">
                            {cats.map((c) => {
                                const active = (form.notification_preferences?.categories || []).includes(c.id);
                                return (
                                    <button type="button" key={c.id} data-testid={`np-${c.id}`} onClick={() => toggleNotifCat(c.id)} className={`pill px-4 py-2 text-sm font-semibold border ${active ? "bg-brand-coral text-white border-brand-coral" : "bg-white text-brand-ink border-brand-line"}`}>{c.name}</button>
                                );
                            })}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label-base">Method</label>
                                <select data-testid="np-method" className="input-base" value={form.notification_preferences?.method || "email"} onChange={(e) => setForm({ ...form, notification_preferences: { ...form.notification_preferences, method: e.target.value } })}>
                                    <option value="email">Email</option>
                                    <option value="in_app">In-app</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-base">Frequency</label>
                                <select data-testid="np-freq" className="input-base" value={form.notification_preferences?.frequency || "immediate"} onChange={(e) => setForm({ ...form, notification_preferences: { ...form.notification_preferences, frequency: e.target.value } })}>
                                    <option value="immediate">Immediate</option>
                                    <option value="daily">Daily digest</option>
                                    <option value="weekly">Weekly digest</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button data-testid="vp-save" disabled={busy} type="submit" className="pill btn-coral px-6 py-3 font-semibold disabled:opacity-50">{busy ? "Saving…" : "Save profile"}</button>
                    </div>
                </form>
            </div>
        </Shell>
    );
}
